import React from "react"

import { useAppState } from "@react-native-community/hooks"
import Toast from "react-native-toast-message"
import useWebSocket, { ReadyState } from "react-use-websocket"

import { isIntentionalContextReplacementInProgress, useContextToken } from "src/utils/contextToken"
import { useAuth } from "./AuthContext"

// This is the time in milliseconds that the app will wait before disconnecting from the server
// if the app is inactive. This is to prevent the server from disconnecting when the app is only
// set to background for a short time and for android system messages.
const WEBSOCKET_KEEPAWAKE = 10000

// The maximum number of times the websocket will attempt to reconnect before giving up.
const MAX_RECONNECT_ATTEMPTS = 7

interface WebsocketContextType
  extends Pick<
    ReturnType<typeof useWebSocket>,
    "sendMessage" | "readyState" | "sendJsonMessage" | "getWebSocket"
  > {
  connectionStatus: string
  isConnected: boolean
  getIsConnected: () => boolean
  error: WebSocketEventMap["error"] | string | null
  setError: React.Dispatch<React.SetStateAction<WebSocketEventMap["error"] | string | null>>
  reconnectAttempts: number
  forceReconnect: () => void
}

export const WebsocketContext = React.createContext<WebsocketContextType | null>(null)

/**
 * WebsocketContext:
 * Provides a WebSocket connection to the app inside a context provider.
 *
 * It uses the react-use-websocket library to manage the WebSocket connection.
 * Read more about it here: https://github.com/robtaussig/react-use-websocket/
 *
 * returns:
 * - sendMessage: function to send a message to the WebSocket connection
 * - sendJsonMessage: function to send a JSON message to the WebSocket connection
 * - readyState: the current state of the WebSocket connection
 * - connectionStatus: a string representation of the current state of the WebSocket connection
 * - isConnected: a boolean indicating if the WebSocket connection is open
 * - getIsConnected: function to check the current state of the connection without waiting for useState propogation
 * - getWebSocket: function to get the WebSocket object
 * - error: the last error event from the WebSocket connection (or null if none)
 * - reconnectAttempts: the number of times the WebSocket has attempted to reconnect
 */
export const WebsocketProvider = ({ children }: React.PropsWithChildren) => {
  const appState = useAppState()
  const appIsActive = appState === "active"
  const {
    getAuthToken,
    initialAuthStateLoaded,
    isAuthenticated,
    apiHost,
    needsRefresh,
    isRefreshing,
    isSessionDeactivated,
    isSponsorInactive,
    markSessionDeactivated,
  } = useAuth()
  // Subscribe reactively to the stored context token. Whenever
  // setContextToken/clearContextToken fires (login bootstrap, role switch,
  // sponsor switch, silent replace), this hook re-renders the provider so
  // useWebSocket re-evaluates its URL factory and the connection is reopened
  // with the new token.
  const contextToken = useContextToken()

  React.useEffect(() => {
    console.log("[WebsocketContext] apiHost:", apiHost)
  }, [apiHost])

  const isConnectedRef = React.useRef(false)
  const [isConnected, setIsConnected] = React.useState(false)
  const [error, setError] = React.useState<WebSocketEventMap["error"] | string | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = React.useState(0)

  // Synchronously update both ref and state to ensure immediate visibility to callers
  // while still triggering re-renders for components using the hook value
  const updateIsConnected = React.useCallback((value: boolean) => {
    isConnectedRef.current = value
    setIsConnected(value)
  }, [])

  // Determine the connection state. If the app has been inactive for WEBSOCKET_KEEPAWAKE, we consider it resigned.
  const [appHasResigned, setAppHasResigned] = React.useState(true) // We start in a resigned state.
  React.useEffect(() => {
    console.log("[WebsocketContext] App state changed appIsActive:", appIsActive)
    let timer: number | null = null
    if (appIsActive === true) {
      setAppHasResigned(false)
    } else {
      console.log(`[WebsocketContext] App is resigning in ${WEBSOCKET_KEEPAWAKE / 1000} seconds.`)
      timer = setTimeout(() => {
        console.log("[WebsocketContext] App has resigned. Websocket disconnecting...")
        setAppHasResigned(true)
      }, WEBSOCKET_KEEPAWAKE)
    }

    return () => {
      if (timer) {
        console.log("[WebsocketContext] Clearing timer")
        clearTimeout(timer)
      }
    }
  }, [appIsActive])

  const shouldBeConnected = React.useMemo(() => {
    // only disconnect if we're refreshing and the token is expired (eg, user returned after a long time)
    // if we're refreshing and the token is not expired yet, we should stay connected (eg, user is still on the app)
    const isRefreshingWithExpiredToken = isRefreshing && needsRefresh()
    const sbc =
      initialAuthStateLoaded &&
      isAuthenticated &&
      !appHasResigned &&
      !error &&
      !isRefreshingWithExpiredToken &&
      !isSessionDeactivated &&
      // Terminal sponsor-gone state: the channel is deleted, so stop trying to
      // reconnect to it while the user picks another channel / logs out.
      !isSponsorInactive &&
      // Backend rejects WebSocket handshakes without a context token
      // (close code 4001). Gate the connection on having one stored —
      // typically populated as the side effect of POST /context/switch
      // (isLogin:true) on bootstrap.
      !!contextToken
    console.log("[WebsocketContext] shouldBeConnected: ", sbc)
    return sbc
  }, [
    initialAuthStateLoaded,
    isAuthenticated,
    appHasResigned,
    error,
    isRefreshing,
    needsRefresh,
    isSessionDeactivated,
    isSponsorInactive,
    contextToken,
  ])

  const getAuthedSocketUrl = React.useCallback(async (): Promise<string> => {
    const socketUrl = `wss://${apiHost}/ws`

    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        throw new Error("No auth token")
      }
      // Handshake requires the context token alongside the Cognito token.
      // Use the value captured in the closure (kept fresh by the useContextToken
      // hook re-rendering the provider) rather than re-reading at call time, so
      // useWebSocket sees a new URL whenever the token changes and reconnects.
      if (!contextToken) {
        throw new Error("No context token")
      }
      const authedUrl =
        `${socketUrl}?token=${encodeURIComponent(`Bearer ${authToken}`)}` +
        `&context_token=${encodeURIComponent(contextToken)}`
      console.log("[WebsocketContext] Authed URL", authedUrl)
      return authedUrl
    } catch (error) {
      console.error("[WebsocketContext] Error building authed socket URL", error)
      return socketUrl
    }
  }, [getAuthToken, apiHost, contextToken])

  const wsOnOpen = React.useCallback(
    (event: WebSocketEventMap["open"]) => {
      console.log("[WebsocketContext] wsOnOpen", event)
      updateIsConnected(true)
      setError(null)
      setReconnectAttempts(0)
    },
    [updateIsConnected],
  )

  const wsOnClose = React.useCallback(
    (event: WebSocketEventMap["close"]) => {
      console.log("[WebsocketContext] wsOnClose", event)
      updateIsConnected(false)
      if (event.code === 4001) {
        // 4001 fires both on a real session takeover AND as an expected
        // side effect of the user's own role/sponsor switch (the
        // freshly minted session_id invalidates the old WS's session). The
        // two cases are distinguished by the intentional-replacement window:
        //
        //   * Window active → an HTTP rotation just stored (or is about to
        //     store) a fresh context_token. useContextToken-driven reconnect
        //     will pick up the new token and re-handshake automatically. Just
        //     clear the error and reset the attempt counter; do nothing else.
        //
        //   * Window inactive → genuine session displacement. Surface the
        //     takeover dialog directly. We deliberately do NOT attempt a
        //     silent POST /context/refresh here: on a real displacement
        //     the backend would remint a token bound to the CURRENT active
        //     session (the displacing tab's), causing both tabs to silently
        //     share one session_id — breaking the exclusive-session
        //     contract. Resume Here (handled by `resumeSession` in
        //     AuthContext) is the user-initiated path that explicitly
        //     calls reactivateContext.
        if (isIntentionalContextReplacementInProgress()) {
          console.log(
            "[WebsocketContext] WS 4001 during intentional context replacement — suppressing",
          )
          setError(null)
          setReconnectAttempts(0)
          return
        }

        console.log("[WebsocketContext] WS closed 4001 — surfacing takeover dialog")
        markSessionDeactivated()
      } else {
        setError(null)
      }
      setReconnectAttempts(0)
    },
    [updateIsConnected, markSessionDeactivated],
  )

  const wsOnError = React.useCallback((event: WebSocketEventMap["error"]) => {
    console.log("[WebsocketContext] wsOnError", event)
    setError((event as any)?.message || "An error occurred.")
  }, [])

  React.useEffect(() => {
    if (reconnectAttempts + 1 >= MAX_RECONNECT_ATTEMPTS) {
      console.log("[WebsocketContext] Maximum reconnect attempts reached. Disconnecting websocket.")
      setError("Maximum reconnect attempts reached.")
    }
  }, [reconnectAttempts])

  const wsShouldReconnect = React.useCallback(
    (_closeEvent: WebSocketEventMap["close"]) => {
      return appIsActive && error === null
    },
    [appIsActive, error],
  )

  const wsOnReconnectStop = React.useCallback(
    (numAttempts: number) => {
      console.log("[WebsocketContext] wsonReconnectStop", numAttempts)
      updateIsConnected(false)
      if (__DEV__) {
        Toast.show({
          type: "error",
          text1: "Server Connection Interrupted",
          text2: "Please check your internet connection and try again.",
          autoHide: false,
        })
      }
    },
    [updateIsConnected],
  )

  const wsMessageFilter = React.useCallback((_event: WebSocketEventMap["message"]) => {
    // This could be useful to filter messages that we don't need coming back from websockets.
    return true
  }, [])

  const {
    sendMessage,
    // jsonMessage must be JSON-parsable
    sendJsonMessage,
    // -1 if uninstantiated, otherwise follows WebSocket readyState mapping: 0: 'Connecting', 1 'OPEN', 2: 'CLOSING', 3: 'CLOSED'
    readyState,
    // If using a shared websocket, return value will be a proxy-wrapped websocket, with certain properties/methods protected
    getWebSocket,
  } = useWebSocket(
    getAuthedSocketUrl,
    {
      onOpen: wsOnOpen,
      onClose: wsOnClose,
      onError: wsOnError,
      shouldReconnect: wsShouldReconnect,
      reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      // attemptNumber will be 0 the first time it attempts to reconnect, so this equation results
      // in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, and then
      // caps at 10 seconds until the maximum number of attempts is reached
      reconnectInterval: (attemptNumber) => {
        setReconnectAttempts(attemptNumber)
        return Math.min(Math.pow(2, attemptNumber) * 1000, 10000)
      },
      onReconnectStop: wsOnReconnectStop,
      filter: wsMessageFilter,
      retryOnError: true,
      // For this to be true, we need a version of react-native that supports the Proxy object
      // It was added in react-native 0.74 with the new arch.
      share: false,
    },

    // we currently only want to connect to websocket when user is authenticated
    shouldBeConnected, // boolean to indicate if the websocket should be connected or not.
  )

  // Set the binaryType of the WebSocket to "arraybuffer" when the connection is established
  React.useEffect(() => {
    if (!isSessionDeactivated && error === "session_deactivated") {
      setError(null)
    }
  }, [isSessionDeactivated, error])

  React.useEffect(() => {
    if (!isConnected) {
      return
    }

    // Solution to set the binaryType of the WebSocket from:
    // https://github.com/robtaussig/react-use-websocket/issues/171#issuecomment-1426364104
    const webSocketLike = getWebSocket()
    if (webSocketLike && "binaryType" in webSocketLike) {
      webSocketLike.binaryType = "arraybuffer"
    }
  }, [getWebSocket, isConnected])

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState]

  React.useEffect(() => {
    console.debug("[WebsocketContext] connectionStatus changed:", connectionStatus)
  }, [connectionStatus])

  // Force reconnect by closing the current websocket connection
  const forceReconnect = React.useCallback(() => {
    console.log("[WebsocketContext] Force reconnecting websocket...")
    const ws = getWebSocket()
    if (ws && readyState === ReadyState.OPEN) {
      // Close the websocket with code 1000 (normal closure)
      // The shouldReconnect callback will handle reconnecting
      updateIsConnected(false)
      ws.close(1000, "Force reconnect")
    } else {
      console.log("[WebsocketContext] Websocket is not open, cannot force reconnect")
    }
  }, [getWebSocket, readyState, updateIsConnected])

  const value = {
    sendMessage,
    sendJsonMessage,
    readyState,
    connectionStatus,
    isConnected,
    getIsConnected: () => isConnectedRef.current,
    getWebSocket,
    error,
    setError,
    reconnectAttempts,
    forceReconnect,
  }

  return <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>
}

export const useWebsocket = () => {
  const context = React.useContext(WebsocketContext)
  if (!context) {
    throw new Error("useWebsocket must be used within an WebsocketProvider")
  }

  return context
}
